import { DEFAULT_AVATAR } from './data'
import type { Avatar, PlayerProfile, WorldDraft } from '../types'

const USER_KEY = 'blockverse:user'
const AVATAR_KEY = 'blockverse:avatar'
const WORLD_KEY = 'blockverse:world'
const FAVORITES_KEY = 'blockverse:favorites'

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function read<T>(key: string, fallback: T): T {
  if (!canUseStorage()) return fallback
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function write<T>(key: string, value: T) {
  if (!canUseStorage()) return
  window.localStorage.setItem(key, JSON.stringify(value))
}

export function loadUser(): PlayerProfile | null {
  return read<PlayerProfile | null>(USER_KEY, null)
}

export function saveUser(user: PlayerProfile) {
  write(USER_KEY, user)
}

export function clearUser() {
  if (canUseStorage()) window.localStorage.removeItem(USER_KEY)
}

export function loadAvatar(): Avatar {
  return read<Avatar>(AVATAR_KEY, DEFAULT_AVATAR)
}

export function saveAvatar(avatar: Avatar) {
  write(AVATAR_KEY, avatar)
}

export function loadWorldDraft(): WorldDraft | null {
  const draft = read<WorldDraft | null>(WORLD_KEY, null)
  if (!draft) return null
  return {
    ...draft,
    name: draft.name ?? 'My first pocket-world',
    description: draft.description ?? 'A small skybound experiment.',
    category: draft.category ?? 'Adventure',
    thumbnail: draft.thumbnail ?? 'sky',
    published: draft.published ?? false,
  }
}

export function saveWorldDraft(draft: WorldDraft) {
  write(WORLD_KEY, draft)
}

export function loadFavorites(): string[] {
  return read<string[]>(FAVORITES_KEY, [])
}

export function toggleFavorite(gameId: string): string[] {
  const current = loadFavorites()
  const next = current.includes(gameId) ? current.filter((id) => id !== gameId) : [...current, gameId]
  write(FAVORITES_KEY, next)
  return next
}
